/*
  Nav drawer behaviour (snippets/drawer-menu.liquid).

  Five small pieces, each documented at its own function:

    resetToRenderedState() the hamburger reopens the drawer as the page built it
    init()                 the hoisted tile strip follows the open section
    syncExpanded()         aria-expanded stays honest through tabs mode
    allowClose()           a second tap on an open section closes it
    closeAboveMobile()     the drawer closes itself when the viewport goes desktop

  On the tile strip: drawer-menu.liquid renders every top-level section's
  coloured tiles once, in a strip at the top of the drawer, each set hidden and
  tagged with the id of the accordion panel it belongs to. init() shows the set
  for whichever section is open, so the strip swaps as the shopper opens SHOP or
  LEARN. (This only runs under the "Above the menu" tile placement; the other
  placements render the tiles inside their own section and need no script.)

  It watches the panels' class attribute rather than listening for clicks: the
  theme's collapsible module owns `is-open`, and a click listener would have to
  guess at the order the two handlers run in. An observer is correct whatever
  opens the panel — including the SHOP section that starts expanded.
*/
(function () {
  function init() {
    var wrapper = document.querySelector('[data-drawer-featured]');
    if (!wrapper) return;

    var pairs = [];
    wrapper.querySelectorAll('[data-featured-for]').forEach(function (set) {
      var panel = document.getElementById(set.getAttribute('data-featured-for'));
      if (panel) pairs.push({ set: set, panel: panel });
    });
    if (!pairs.length) return;

    // The panel whose tiles are showing. Kept across syncs so that when two
    // sections happen to be open at once the strip doesn't flip back and forth.
    var current = null;

    function isOpen(pair) {
      return pair.panel.classList.contains('is-open');
    }

    function apply() {
      var open = pairs.filter(isOpen);

      if (!open.length) {
        current = null;
      } else if (!open.some(function (pair) { return pair.panel === current; })) {
        current = open[open.length - 1].panel;
      }

      pairs.forEach(function (pair) {
        pair.set.hidden = pair.panel !== current;
      });
    }

    var observer = new MutationObserver(function (mutations) {
      // A section that just opened takes the strip off whatever held it.
      for (var i = mutations.length - 1; i >= 0; i--) {
        var target = mutations[i].target;
        if (target.classList.contains('is-open')) {
          current = target;
          break;
        }
      }
      apply();
    });

    pairs.forEach(function (pair) {
      observer.observe(pair.panel, { attributes: true, attributeFilter: ['class'] });
    });

    apply();
  }

  /*
    Keep aria-expanded honest on the top-level sections.

    The collapsible module sets aria-expanded on the trigger it toggles, but in
    tabs mode it closes the other triggers by stripping `is-open` straight off
    them — their aria-expanded keeps claiming "true" while the panel is shut.
    Mirroring the class back onto the attribute costs nothing and keeps the
    drawer honest to a screen reader.
  */
  function syncExpanded() {
    var triggers = document.querySelectorAll('.collapsible-trigger--tab');
    if (!triggers.length) return;

    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        var trigger = mutation.target;
        trigger.setAttribute('aria-expanded', trigger.classList.contains('is-open'));
      });
    });

    triggers.forEach(function (trigger) {
      trigger.setAttribute('aria-expanded', trigger.classList.contains('is-open'));
      observer.observe(trigger, { attributes: true, attributeFilter: ['class'] });
    });
  }

  /*
    Let an open section close again.

    The top-level sections run as one accordion — a shared data-id plus
    .collapsible-trigger--tab — so opening SHOP closes LEARN. The theme's
    collapsible module implements that mode by refusing to act on a trigger that
    is already open ("if (isTab && isOpen) return"), which is right for tabs,
    where something always has to be selected, and wrong for a menu: tapping the
    section you just opened did nothing at all.

    Rather than drop tabs mode — which would let both sections stand open and
    make the drawer twice as long — take the trigger out of the group for the
    length of one click. The module then sees a plain collapsible, closes it with
    its own animation and its own height bookkeeping, and the class goes back
    before anything else can read it.

    Capture phase, so this runs before the module's own listener, which is bound
    on the trigger itself. Restoring on a timeout puts the class back after that
    listener has returned.
  */
  function allowClose() {
    var drawer = document.getElementById('NavDrawer');
    if (!drawer) return;

    drawer.addEventListener('click', function (evt) {
      var trigger = evt.target.closest('.collapsible-trigger--tab');
      if (!trigger || !trigger.classList.contains('is-open')) return;

      trigger.classList.remove('collapsible-trigger--tab');
      setTimeout(function () {
        trigger.classList.add('collapsible-trigger--tab');
      }, 0);
    }, true);
  }

  /*
    Close the drawer once the viewport is wide enough for the desktop nav.

    769px is where .medium-up--hide takes the hamburger away and the mega menu
    takes over, so above it the drawer is a menu with no way back: its own
    close button is styled in a max-width:768px block, and the theme puts
    `js-drawer-open` (overflow: hidden) on <html> for as long as it is open, so
    a drawer left open across the breakpoint is a panel the shopper cannot
    dismiss on a page that will not scroll. Rotating a tablet is enough to land
    there.

    theme.NavDrawer.close() rather than stripping the class by hand: it also
    clears that overflow lock, releases the focus trap and puts focus back on
    the source. Called with no argument — close(evt) deliberately ignores
    events that came from inside the drawer.

    Looked up when the query fires, not cached at start: this file is deferred
    and theme.NavDrawer is built in the header section's own init, so which
    lands first is not ours to assume.

    The theme has its own matchSmall/unmatchSmall events, but they hang off
    `max-width: 769px` — one pixel wide of the breakpoint the layout actually
    uses, which would leave the drawer open at exactly 769px with the hamburger
    already gone.
  */
  function closeAboveMobile() {
    var desktop = window.matchMedia('(min-width: 769px)');

    function onChange(mql) {
      if (!mql.matches) return;
      var drawer = window.theme && window.theme.NavDrawer;
      if (drawer && drawer.isOpen) drawer.close();
    }

    // addEventListener on a MediaQueryList is Safari 14+; addListener is the
    // deprecated spelling that older iOS still needs.
    if (desktop.addEventListener) {
      desktop.addEventListener('change', onChange);
    } else {
      desktop.addListener(onChange);
    }
  }

  /*
    Every tap of the hamburger opens the drawer exactly as the page rendered it.

    The drawer is built once and then reused, so on its own it reopens wherever
    the shopper last left it: open LEARN, close the drawer, open it again and you
    are still looking at LEARN, three levels deep in whatever you were reading.
    drawer-menu.liquid's opening state only ever reached the first open of a page
    load.

    What gets restored is a snapshot of that rendered state, taken before
    anything can have been tapped — not a hardcoded "SHOP open, everything else
    shut". The two are not the same thing: alongside SHOP, drawer-menu.liquid
    also expands the child section holding the page being viewed
    (childlink.active / child_active), so on a collection page under STAMPING &
    CORE CRAFTING a fresh load arrives with that child open too. Closing it on
    reset would put the shopper somewhere the page never actually renders.
    Snapshotting also means this needs no marker in the markup and no updating
    when the Liquid changes its mind about what opens.

    Capture phase on the hamburger, so this runs before theme.Drawers.open()
    rather than off the drawerOpen event, which fires once the drawer is already
    sliding in. Both panel properties that can animate — opacity over 1s, the
    inner's translate over 0.5s — measured as settled 54ms after the tap, well
    inside the drawer's own 450ms slide, so the sections are simply in place when
    the drawer arrives rather than unfolding in front of the shopper. Move this
    to the bubble phase or to drawerOpen and that stops being true.
  */
  function resetToRenderedState() {
    var drawer = document.getElementById('NavDrawer');
    if (!drawer) return;

    // Every level, not just the top-level tabs: .collapsible-trigger covers the
    // section headings, their child rows and the grandchild lists alike.
    var initial = [];
    drawer.querySelectorAll('.collapsible-trigger').forEach(function (trigger) {
      var panel = document.getElementById(trigger.getAttribute('aria-controls'));
      if (!panel) return;
      initial.push({
        trigger: trigger,
        panel: panel,
        open: trigger.classList.contains('is-open')
      });
    });
    if (!initial.length) return;

    function reset() {
      initial.forEach(function (entry) {
        entry.trigger.classList.toggle('is-open', entry.open);
        entry.trigger.setAttribute('aria-expanded', entry.open ? 'true' : 'false');
        entry.panel.classList.toggle('is-open', entry.open);

        // Both resting heights belong to the stylesheet — `height: auto
        // !important` on .collapsible-content.is-open, `height: 0` on
        // .collapsible-content--all. The inline height only exists because the
        // collapsible module animates with it, so clearing it is the whole job.
        entry.panel.style.height = '';
      });
    }

    document.addEventListener('click', function (evt) {
      if (evt.target.closest('.js-drawer-open-nav')) reset();
    }, true);
  }

  function start() {
    // Before anything else: resetToRenderedState() snapshots the drawer's
    // opening state, so nothing may have toggled a section by the time it runs.
    resetToRenderedState();
    init();
    syncExpanded();
    allowClose();
    closeAboveMobile();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
